import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Howler } from 'howler';
import musicSrc from '@/assets/sounds/suigar-ost.mp3';

const MUSIC_SRC = musicSrc;
const MUSIC_VOLUME = 0.5;
const SOUND_COOKIE_KEY = 'suigar.preferences.sound';
const SOUND_COOKIE_COMPAT_KEY = 'suigar.preferences.sfx';

type AudioContextValue = {
	isMusicEnabled: boolean;
	isMusicPlaying: boolean;
	playMusic: () => Promise<void>;
	pauseMusic: () => void;
	toggleMusic: () => Promise<void>;
	setMusicEnabled: (enabled: boolean) => Promise<void>;
	isSoundEffectsEnabled: boolean;
	toggleSoundEffects: () => void;
	setSoundEffectsEnabled: (enabled: boolean) => void;
};

const AudioContext = createContext<AudioContextValue | undefined>(undefined);

const readSoundPreference = (): boolean => {
	if (typeof document === 'undefined') return true;
	const cookies = document.cookie.split('; ').reduce<Record<string, string>>((acc, cookie) => {
		const [key, ...rest] = cookie.split('=');
		if (key) {
			acc[key] = rest.join('=');
		}
		return acc;
	}, {});

	if (cookies[SOUND_COOKIE_KEY] !== undefined) {
		return cookies[SOUND_COOKIE_KEY] === 'true';
	}

	if (cookies[SOUND_COOKIE_COMPAT_KEY] !== undefined) {
		return cookies[SOUND_COOKIE_COMPAT_KEY] === '1';
	}

	return true;
};

const writeSoundPreference = (value: boolean) => {
	if (typeof document === 'undefined') return;
	const maxAge = 365 * 24 * 60 * 60; // 1 year
	document.cookie = `${SOUND_COOKIE_KEY}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
	document.cookie = `${SOUND_COOKIE_COMPAT_KEY}=${value ? '1' : '0'}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

export function AudioProvider({ children }: { children: ReactNode }) {
	const musicRef = useRef<HTMLAudioElement | null>(null);
	const listenersRef = useRef<{
		play: () => void;
		pause: () => void;
		ended: () => void;
	} | null>(null);
	const [isMusicEnabled, setIsMusicEnabled] = useState(false);
	const [isMusicPlaying, setIsMusicPlaying] = useState(false);
	const [isSoundEffectsEnabled, setIsSoundEffectsEnabled] = useState(readSoundPreference);

	const teardownAudio = useCallback(() => {
		const audio = musicRef.current;
		const listeners = listenersRef.current;
		if (audio && listeners) {
			audio.removeEventListener('play', listeners.play);
			audio.removeEventListener('pause', listeners.pause);
			audio.removeEventListener('ended', listeners.ended);
		}
		if (audio) {
			audio.pause();
			audio.src = '';
		}
		musicRef.current = null;
		listenersRef.current = null;
		setIsMusicPlaying(false);
	}, []);

	const ensureAudioElement = useCallback((): HTMLAudioElement | null => {
		if (typeof window === 'undefined') return null;
		if (musicRef.current) return musicRef.current;

		const audio = new Audio(MUSIC_SRC);
		audio.loop = true;
		audio.preload = 'auto';
		audio.volume = MUSIC_VOLUME;
		audio.autoplay = false;
		audio.muted = true;

		const handlePlay = () => setIsMusicPlaying(true);
		const handlePause = () => setIsMusicPlaying(false);
		const handleEnded = () => setIsMusicPlaying(false);

		audio.addEventListener('play', handlePlay);
		audio.addEventListener('pause', handlePause);
		audio.addEventListener('ended', handleEnded);

		musicRef.current = audio;
		listenersRef.current = { play: handlePlay, pause: handlePause, ended: handleEnded };

		return audio;
	}, []);

	useEffect(() => {
		return () => {
			teardownAudio();
		};
	}, [teardownAudio]);

	useEffect(() => {
		if (isSoundEffectsEnabled) {
			Howler.mute(false);
		} else {
			Howler.mute(true);
		}
		writeSoundPreference(isSoundEffectsEnabled);
	}, [isSoundEffectsEnabled]);

	useEffect(() => {
		const audio = musicRef.current;
		if (!audio) return;
		audio.muted = !isMusicEnabled;
		if (!isMusicEnabled && !audio.paused) {
			audio.pause();
		}
	}, [isMusicEnabled]);

	const setMusicEnabled = useCallback(
		async (enabled: boolean) => {
			setIsMusicEnabled(enabled);
			const audio = ensureAudioElement();
			if (!audio) return;

			audio.muted = !enabled;

			if (enabled) {
				try {
					if (audio.paused) {
						await audio.play();
					}
				} catch (error) {
					console.warn('Music playback prevented by browser policies.', error);
					setIsMusicEnabled(false);
				}
			} else if (!audio.paused) {
				audio.pause();
			}
		},
		[ensureAudioElement],
	);

	const playMusic = useCallback(async () => {
		await setMusicEnabled(true);
	}, [setMusicEnabled]);

	const pauseMusic = useCallback(() => {
		void setMusicEnabled(false);
	}, [setMusicEnabled]);

	const toggleMusic = useCallback(async () => {
		await setMusicEnabled(!isMusicEnabled);
	}, [isMusicEnabled, setMusicEnabled]);

	const toggleSoundEffects = useCallback(() => {
		setIsSoundEffectsEnabled((prev) => !prev);
	}, []);

	const value = useMemo<AudioContextValue>(
		() => ({
			isMusicEnabled,
			isMusicPlaying,
			playMusic,
			pauseMusic,
			toggleMusic,
			setMusicEnabled,
			isSoundEffectsEnabled,
			toggleSoundEffects,
			setSoundEffectsEnabled: setIsSoundEffectsEnabled,
		}),
		[isMusicEnabled, isMusicPlaying, playMusic, pauseMusic, toggleMusic, setMusicEnabled, isSoundEffectsEnabled, toggleSoundEffects],
	);

	return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
}

export function useAudio() {
	const context = useContext(AudioContext);
	if (!context) {
		throw new Error('useAudio must be used within an AudioProvider');
	}
	return context;
}
